import type { CaptureResult, PostHog } from 'posthog-js'
import { Environment } from './Environment'

export type ClickData = Record<string, string | number | boolean>

// Time zones outside the Europe/ prefix that still fall under EU/EEA privacy rules.
const cookielessTimeZones = ['Atlantic/Reykjavik', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Azores', 'Asia/Nicosia', 'Asia/Famagusta']

// Query parameters that must never reach analytics: page URLs carry invite codes
// (/groupinvites/:id/accept) and approval tokens (/signin/approve).
const redactedUrlParams = ['code', 'approval_token', 'clientSecret', 'token']

const redactedUrlProperties = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']

let posthogPromise: Promise<PostHog | undefined> | undefined

// Visitors here get cookieless tracking and no session replay. Matching on time zone is
// deliberately over-inclusive — Europe/ also covers Switzerland, Norway, Turkey and Russia —
// because treating a non-EU visitor as EU is harmless, while the reverse is not.
export function isCookielessRegion() {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
        return timeZone.startsWith('Europe/') || cookielessTimeZones.includes(timeZone)
    }
    catch {
        return true
    }
}

// Safe to call before the app has painted: the SDK loads in its own chunk, and clicks tracked
// before it arrives are held by the promise rather than dropped.
// The host is required, not defaulted: falling back to PostHog's own domain would silently
// give up the ad-blocker resistance the proxy subdomain exists to provide.
export function initAnalytics() {
    const apiKey = Environment.get('POSTHOG_KEY')
    const apiHost = Environment.get('POSTHOG_HOST')
    if (!apiKey || !apiHost || posthogPromise) {
        return
    }
    posthogPromise = loadPostHogAsync(apiKey, apiHost)
}

export function trackClick(eventName: string, data: ClickData = {}) {
    posthogPromise?.then(posthog => posthog?.capture(eventName, data))
}

// App Insights auto-tracks every analytics request as a dependency, which logs each one to the
// console and bills it as telemetry. Analytics traffic isn't app diagnostics, so we instruct main.ts to drop it.
export function isAnalyticsRequest(target?: string) {
    const host = Environment.get('POSTHOG_HOST')
    return !!target && !!host && target.includes(host.replace(/^https?:\/\//, ''))
}

async function loadPostHogAsync(apiKey: string, apiHost: string) {
    try {
        // Unnamed on purpose: a named chunk would put "posthog" in the script URL, which ad
        // blockers match on even when it is served first-party.
        const { default: posthog } = await import('posthog-js')
        const cookieless = isCookielessRegion()
        posthog.init(apiKey, {
            api_host: apiHost,
            ui_host: 'https://us.posthog.com',
            defaults: '2026-08-30',
            autocapture: false,
            capture_pageview: 'history_change',
            persistence: cookieless ? 'memory' : 'localStorage',
            cookieless_mode: cookieless ? 'always' : undefined,
            person_profiles: cookieless ? 'never' : 'identified_only',
            disable_session_recording: cookieless || Environment.get('SESSION_REPLAY') != 'true',
            before_send: redactEvent,
        })
        return posthog
    }
    catch {
        return undefined // analytics is never worth a console error
    }
}

function redactEvent(event: CaptureResult | null) {
    if (event?.properties) {
        for (const name of redactedUrlProperties) {
            const value = event.properties[name]
            if (typeof value == 'string') {
                event.properties[name] = redactUrl(value)
            }
        }
    }
    return event
}

function redactUrl(value: string) {
    try {
        const url = new URL(value)
        let redacted = false
        for (const p of redactedUrlParams) {
            if (url.searchParams.has(p)) {
                url.searchParams.set(p, 'REDACTED')
                redacted = true
            }
        }
        return redacted ? url.toString() : value
    }
    catch {
        return value // not a URL (e.g. the "$direct" referrer)
    }
}
